/**
 * CRM Influencers — discovery, list, and pipeline kanban for influencer outreach.
 *
 * Three views switchable via tabs:
 * 1. Pool — list of saved influencers with filters + pipeline kanban
 * 2. Discover — search external influencer database (Influencers.club)
 * 3. Campaigns — manage outreach campaigns
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Badge,
  Button,
  EmptyState,
  SkeletonRow,
  SkeletonCard,
  Select,
  Input,
  Tabs,
  SearchInput,
  Dialog,
  Textarea,
  Spinner,
  ConfirmDialog,
  Pagination,
} from '../../components/ui';
import {
  Megaphone,
  Plus,
  Search,
  Users,
  ExternalLink,
  Mail,
  LayoutGrid,
  List,
  Trash2,
  Edit3,
  UserPlus,
} from '../../lib/icons';
import { usePersistedPageSize } from '../../hooks/use-persisted-page-size';
import {
  fetchInfluencers,
  fetchInfluencerStats,
  discoverInfluencers,
  createInfluencer,
  updateInfluencer,
  deleteInfluencer,
  fetchCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
} from '../../lib/crm-influencer-api';
import type { CrmInfluencer, DiscoveryResult, InfluencerStats, CrmCampaign } from '../../lib/crm-influencer-api';
import { toast } from '../../components/ui';

// ─── Constants ───────────────────────────────────────────

const STATUS_ORDER = ['prospect', 'contacted', 'replied', 'in_progress', 'completed', 'declined'];
const STATUS_LABELS: Record<string, string> = {
  prospect: 'Prospect',
  contacted: 'Contacted',
  replied: 'Replied',
  in_progress: 'In Progress',
  completed: 'Completed',
  declined: 'Declined',
  blacklisted: 'Blacklisted',
};
const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  prospect: 'default',
  contacted: 'secondary',
  replied: 'warning',
  in_progress: 'warning',
  completed: 'success',
  declined: 'destructive',
  blacklisted: 'destructive',
};

const PLATFORM_LABELS: Record<string, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X / Twitter',
};

const CAMPAIGN_STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  planning: 'default',
  active: 'warning',
  completed: 'success',
  cancelled: 'destructive',
};

function formatFollowers(n: number | null): string {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function platformIcon(p: string): string {
  const map: Record<string, string> = { youtube: '📺', instagram: '📷', tiktok: '🎵', x: '𝕏' };
  return map[p] || '🌐';
}

// ─── Main Component ──────────────────────────────────────

export function InfluencersPage() {
  const [activeTab, setActiveTab] = useState<'pool' | 'discover' | 'campaigns'>('pool');

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tab bar */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2 flex items-center gap-4 flex-shrink-0">
        <Tabs
          tabs={[
            { key: 'pool', label: 'Influencer Pool' },
            { key: 'discover', label: 'Discover' },
            { key: 'campaigns', label: 'Campaigns' },
          ]}
          active={activeTab}
          onChange={(k) => setActiveTab(k as any)}
        />
      </div>

      <div className="flex-1 overflow-hidden">
        {activeTab === 'pool' && <InfluencerPoolTab />}
        {activeTab === 'discover' && <DiscoverTab />}
        {activeTab === 'campaigns' && <CampaignsTab />}
      </div>
    </div>
  );
}

// ─── Pool Tab ────────────────────────────────────────────

function InfluencerPoolTab() {
  const [influencers, setInfluencers] = useState<CrmInfluencer[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<InfluencerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedPageSize('crm.influencers', 20);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const limit = viewMode === 'kanban' ? 200 : pageSize;
      const offset = viewMode === 'kanban' ? 0 : page * pageSize;
      const [res, s] = await Promise.all([
        fetchInfluencers({
          search: search || undefined,
          platform: platform || undefined,
          status: status || undefined,
          limit,
          offset,
        }),
        fetchInfluencerStats(),
      ]);
      setInfluencers(res.items);
      setTotal(res.total);
      setStats(s);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [search, platform, status, page, viewMode, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    setPage(0);
  }, [search, platform, status, pageSize]);

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await updateInfluencer(id, { status: newStatus } as any);
      toast('Status updated', 'success');
      loadData();
    } catch {
      toast('Failed to update', 'error');
    }
    setEditingId(null);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteInfluencer(id);
      toast('Influencer removed', 'success');
      loadData();
    } catch {
      toast('Failed to delete', 'error');
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Stats cards */}
      {stats && stats.total > 0 && (
        <div className="flex gap-3 px-4 py-3 overflow-x-auto flex-shrink-0">
          <StatCard label="Total" value={stats.total} />
          {stats.by_status
            .filter((s) => ['prospect', 'contacted', 'replied', 'in_progress', 'completed'].includes(s.status))
            .map((s) => (
              <StatCard key={s.status} label={STATUS_LABELS[s.status] || s.status} value={s.count} />
            ))}
          <StatCard label="Contacted (month)" value={stats.contacted_this_month} />
          <StatCard label="Replied (month)" value={stats.replied_this_month} />
        </div>
      )}

      {/* Filters */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2.5 flex items-center gap-2 flex-wrap flex-shrink-0">
        <SearchInput
          size="sm"
          className="flex-1 min-w-[120px] sm:flex-none sm:w-[180px]"
          placeholder="Search influencers..."
          value={search}
          onChange={setSearch}
        />
        <Select size="sm" inline value={platform} onChange={(e) => setPlatform(e.target.value)}>
          <option value="">All Platforms</option>
          <option value="youtube">YouTube</option>
          <option value="instagram">Instagram</option>
          <option value="tiktok">TikTok</option>
          <option value="x">X / Twitter</option>
        </Select>
        <Select size="sm" inline value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All Status</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        <div className="flex-1" />
        <button
          onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')}
          className="p-1.5 rounded-md hover:bg-surface-sunken text-fg-muted"
          title={viewMode === 'list' ? 'Kanban view' : 'List view'}
        >
          {viewMode === 'list' ? <LayoutGrid size={14} /> : <List size={14} />}
        </button>
        <span className="text-xs text-fg-faint">{total} results</span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonRow key={i} cols={8} />
                ))}
              </tbody>
            </table>
          </div>
        ) : influencers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No influencers yet"
            description="Use the Discover tab to find and add influencers to your pool."
          />
        ) : viewMode === 'list' ? (
          <table className="w-full min-w-[800px] [&_td]:whitespace-nowrap">
            <thead className="sticky top-0 z-10 bg-surface-raised [&_th]:whitespace-nowrap">
              <tr className="border-b border-edge text-xs text-fg-muted">
                <th className="text-left px-4 py-2 font-medium">Influencer</th>
                <th className="text-left px-3 py-2 font-medium">Platform</th>
                <th className="text-right px-3 py-2 font-medium">Followers</th>
                <th className="text-right px-3 py-2 font-medium">Engagement</th>
                <th className="text-left px-3 py-2 font-medium">Country</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium hidden lg:table-cell">Email</th>
                <th className="text-right px-4 py-2 font-medium w-[80px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {influencers.map((inf) => (
                <tr key={inf.id} className="border-b border-edge hover:bg-surface-sunken transition-colors group">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {inf.avatar_url ? (
                        <img src={inf.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-surface-muted flex items-center justify-center text-xs font-medium text-fg-muted flex-shrink-0">
                          {inf.name.charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-fg truncate" title={inf.name}>
                          {inf.name}
                        </div>
                        <div className="text-[10px] text-fg-faint truncate" title={inf.platform_handle}>
                          @{inf.platform_handle}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {platformIcon(inf.platform)} {PLATFORM_LABELS[inf.platform] || inf.platform}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-right font-medium">{formatFollowers(inf.follower_count)}</td>
                  <td className="px-3 py-2.5 text-xs text-right">
                    {inf.engagement_rate ? `${inf.engagement_rate.toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-fg-secondary">{inf.country || '—'}</td>
                  <td className="px-3 py-2.5">
                    {editingId === inf.id ? (
                      <Select
                        size="xs"
                        inline
                        value={editStatus}
                        onChange={(e) => {
                          handleStatusChange(inf.id, e.target.value);
                        }}
                      >
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingId(inf.id);
                          setEditStatus(inf.status);
                        }}
                        title="Click to change"
                      >
                        <Badge variant={STATUS_VARIANTS[inf.status] || 'default'}>
                          {STATUS_LABELS[inf.status] || inf.status}
                        </Badge>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs hidden lg:table-cell">
                    {inf.email ? (
                      <a
                        href={`mailto:${inf.email}`}
                        className="text-primary-fg hover:underline truncate block max-w-[160px]"
                        title={inf.email}
                      >
                        {inf.email}
                      </a>
                    ) : (
                      <span className="text-fg-faint">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 touch-visible transition-opacity">
                      {inf.platform_url && (
                        <a
                          href={inf.platform_url}
                          target="_blank"
                          rel="noopener"
                          className="p-1 rounded hover:bg-surface-muted text-fg-muted"
                          title="Open profile"
                        >
                          <ExternalLink size={13} />
                        </a>
                      )}
                      <button
                        onClick={() => setDeleteConfirm(inf.id)}
                        className="p-1 rounded hover:bg-surface-muted text-danger"
                        title="Remove"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          /* Kanban view */
          <div className="flex gap-3 p-4 overflow-x-auto h-full">
            {STATUS_ORDER.map((st) => {
              const items = influencers.filter((i) => i.status === st);
              return (
                <div key={st} className="flex-shrink-0 w-[260px] flex flex-col bg-surface-sunken rounded-lg">
                  <div className="px-3 py-2 flex items-center gap-2 border-b border-edge">
                    <Badge variant={STATUS_VARIANTS[st] || 'default'}>{STATUS_LABELS[st]}</Badge>
                    <span className="text-[10px] text-fg-faint">{items.length}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {items.map((inf) => (
                      <Card key={inf.id} className="p-2.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          {inf.avatar_url ? (
                            <img src={inf.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-surface-muted flex items-center justify-center text-[10px] font-medium text-fg-muted">
                              {inf.name.charAt(0)}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium truncate">{inf.name}</div>
                            <div className="text-[10px] text-fg-faint">@{inf.platform_handle}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-fg-muted">
                          <span>{platformIcon(inf.platform)}</span>
                          <span>{formatFollowers(inf.follower_count)}</span>
                          {inf.engagement_rate && <span>{inf.engagement_rate.toFixed(1)}%</span>}
                        </div>
                        {inf.email && (
                          <div className="mt-1 text-[10px] text-primary-fg truncate">
                            <Mail size={10} className="inline mr-1" />
                            {inf.email}
                          </div>
                        )}
                      </Card>
                    ))}
                    {items.length === 0 && <div className="text-center text-xs text-fg-faint py-4">No items</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination (list mode only) */}
      {viewMode === 'list' && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Remove Influencer"
        description="Are you sure you want to remove this influencer from the pool?"
        confirmLabel="Remove"
        confirmVariant="destructive"
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
      />
    </div>
  );
}

// ─── Discover Tab ────────────────────────────────────────

function DiscoverTab() {
  const [results, setResults] = useState<DiscoveryResult[]>([]);
  const [_total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [platform, setPlatform] = useState('youtube');
  const [country, setCountry] = useState('');
  const [minFollowers, setMinFollowers] = useState('');
  const [maxFollowers, setMaxFollowers] = useState('');
  const [addingHandle, setAddingHandle] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const doSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await discoverInfluencers({
        keyword: keyword || undefined,
        platform: platform || undefined,
        country: country || undefined,
        min_followers: minFollowers ? parseInt(minFollowers, 10) : undefined,
        max_followers: maxFollowers ? parseInt(maxFollowers, 10) : undefined,
      });
      setResults(res.items);
      setTotal(res.total);
    } catch (err) {
      // Surfaces the server's explicit not-configured error (no mock fallback anymore)
      toast(err instanceof Error ? err.message : 'Search failed', 'error');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (r: DiscoveryResult) => {
    setAddingHandle(r.platform_handle);
    try {
      await createInfluencer({
        platform: r.platform,
        platform_handle: r.platform_handle.replace(/^@/, ''),
        platform_url: r.platform_url,
        name: r.name,
        avatar_url: r.avatar_url,
        email: r.email,
        follower_count: r.follower_count,
        engagement_rate: r.engagement_rate,
        country: r.country,
        language: r.language,
        niche: r.niche,
        bio: r.bio,
      } as any);
      toast(`${r.name} added to pool`, 'success');
    } catch (err: any) {
      if (err.message?.includes('already exists')) {
        toast('Already in pool', 'warning');
      } else {
        toast('Failed to add', 'error');
      }
    }
    setAddingHandle(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Search form */}
      <div className="border-b border-edge bg-surface-raised px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            size="sm"
            className="flex-1 min-w-[140px] sm:flex-none sm:w-[220px]"
            placeholder="e.g. indoor gardening, hydroponics..."
            value={keyword}
            onChange={setKeyword}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          />
          <Select size="sm" inline value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="youtube">YouTube</option>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="x">X / Twitter</option>
            <option value="twitch">Twitch</option>
          </Select>
          <Input
            size="sm"
            className="w-[100px]"
            placeholder="Min followers"
            value={minFollowers}
            onChange={(e) => setMinFollowers(e.target.value)}
          />
          <Input
            size="sm"
            className="w-[100px]"
            placeholder="Max followers"
            value={maxFollowers}
            onChange={(e) => setMaxFollowers(e.target.value)}
          />
          <Input
            size="sm"
            className="w-[100px]"
            placeholder="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
          <Button size="sm" onClick={doSearch} disabled={loading}>
            {loading ? <Spinner /> : <Search size={14} />}
            <span className="ml-1">Search</span>
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-auto p-4">
        {!searched ? (
          <EmptyState
            icon={Search}
            title="Discover Influencers"
            description="Search 340M+ creator profiles across YouTube, Instagram, TikTok, and more. Enter keywords related to your product niche to get started."
          />
        ) : loading ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : results.length === 0 ? (
          <EmptyState icon={Search} title="No results" description="Try different keywords or broaden your filters." />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {results.map((r) => (
              <Card key={`${r.platform}-${r.platform_handle}`} className="p-4">
                <div className="flex items-start gap-3">
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-surface-muted flex items-center justify-center text-sm font-medium text-fg-muted flex-shrink-0">
                      {r.name.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-fg truncate">{r.name}</span>
                      {r.platform_url && (
                        <a
                          href={r.platform_url}
                          target="_blank"
                          rel="noopener"
                          className="text-fg-faint hover:text-fg-secondary flex-shrink-0"
                        >
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-fg-muted">
                      {platformIcon(r.platform)} @{r.platform_handle}
                    </div>
                  </div>
                </div>

                <div className="mt-2.5 flex items-center gap-3 text-xs text-fg-secondary">
                  <span title="Followers">
                    <strong>{formatFollowers(r.follower_count)}</strong> followers
                  </span>
                  <span title="Engagement rate">
                    <strong>{r.engagement_rate.toFixed(1)}%</strong> engagement
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-fg-faint">
                  <span>{r.country}</span>
                  <span>·</span>
                  <span>{r.language}</span>
                </div>

                {r.bio && <p className="mt-2 text-xs text-fg-secondary line-clamp-2">{r.bio}</p>}

                <div className="mt-3 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    disabled={addingHandle === r.platform_handle}
                    onClick={() => handleAdd(r)}
                  >
                    {addingHandle === r.platform_handle ? <Spinner /> : <UserPlus size={13} />}
                    <span className="ml-1">Add to Pool</span>
                  </Button>
                  {r.email && (
                    <span className="text-[10px] text-success flex items-center gap-0.5">
                      <Mail size={10} /> Email available
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Campaigns Tab ───────────────────────────────────────

function CampaignsTab() {
  const [campaigns, setCampaigns] = useState<CrmCampaign[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CrmCampaign | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCampaigns({ limit: 50 });
      setCampaigns(res.items);
      setTotal(res.total);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (data: Partial<CrmCampaign>) => {
    try {
      if (editingCampaign) {
        await updateCampaign(editingCampaign.id, data);
        toast('Campaign updated', 'success');
      } else {
        await createCampaign(data);
        toast('Campaign created', 'success');
      }
      setFormOpen(false);
      setEditingCampaign(null);
      loadData();
    } catch {
      toast('Failed to save', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCampaign(id);
      toast('Campaign deleted', 'success');
      loadData();
    } catch {
      toast('Failed to delete', 'error');
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2.5 flex items-center gap-2 flex-shrink-0">
        <div className="flex-1" />
        <span className="text-xs text-fg-faint">{total} campaigns</span>
        <Button
          size="sm"
          onClick={() => {
            setEditingCampaign(null);
            setFormOpen(true);
          }}
        >
          <Plus size={14} /> <span className="ml-1">New Campaign</span>
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="No campaigns yet"
            description="Create a campaign to organize your influencer outreach by product or promotion."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {campaigns.map((c) => (
              <Card key={c.id} className="p-4 group">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-fg truncate">{c.name}</h3>
                    {c.product_name && <div className="text-xs text-fg-muted mt-0.5">{c.product_name}</div>}
                  </div>
                  <Badge variant={CAMPAIGN_STATUS_VARIANTS[c.status] || 'default'}>
                    {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                  </Badge>
                </div>
                {c.description && <p className="text-xs text-fg-secondary line-clamp-2 mb-2">{c.description}</p>}
                <div className="flex items-center gap-3 text-[10px] text-fg-faint">
                  <span>
                    <Users size={10} className="inline mr-0.5" />
                    {c.influencer_count || 0} influencers
                  </span>
                  {c.budget && <span>${c.budget.toLocaleString()}</span>}
                  {c.start_date && <span>{c.start_date}</span>}
                </div>
                <div className="mt-3 flex gap-1 opacity-0 group-hover:opacity-100 touch-visible transition-opacity">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingCampaign(c);
                      setFormOpen(true);
                    }}
                  >
                    <Edit3 size={12} /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDeleteConfirm(c.id)}>
                    <Trash2 size={12} /> Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Campaign form dialog */}
      <CampaignFormDialog
        open={formOpen}
        campaign={editingCampaign}
        onSave={handleSave}
        onClose={() => {
          setFormOpen(false);
          setEditingCampaign(null);
        }}
      />

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Campaign"
        description="Are you sure? Influencers linked to this campaign will be unlinked but not deleted."
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
      />
    </div>
  );
}

// ─── Campaign Form Dialog ────────────────────────────────

function CampaignFormDialog({
  open,
  campaign,
  onSave,
  onClose,
}: {
  open: boolean;
  campaign: CrmCampaign | null;
  onSave: (data: Partial<CrmCampaign>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [productName, setProductName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('planning');
  const [budget, setBudget] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [targetCount, setTargetCount] = useState('');

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setProductName(campaign.product_name || '');
      setDescription(campaign.description || '');
      setStatus(campaign.status);
      setBudget(campaign.budget ? String(campaign.budget) : '');
      setStartDate(campaign.start_date || '');
      setEndDate(campaign.end_date || '');
      setTargetCount(campaign.target_count ? String(campaign.target_count) : '');
    } else {
      setName('');
      setProductName('');
      setDescription('');
      setStatus('planning');
      setBudget('');
      setStartDate('');
      setEndDate('');
      setTargetCount('');
    }
  }, [campaign, open]);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      product_name: productName || undefined,
      description: description || undefined,
      status,
      budget: budget ? parseFloat(budget) : undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      target_count: targetCount ? parseInt(targetCount, 10) : undefined,
    } as any);
  };

  return (
    <Dialog open={open} onClose={onClose} title={campaign ? 'Edit Campaign' : 'New Campaign'}>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-fg-muted block mb-1">Name *</label>
          <Input
            size="sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. LPH-Max Summer Launch"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-fg-muted block mb-1">Product</label>
          <Input
            size="sm"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="e.g. LPH-Max"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-fg-muted block mb-1">Description</label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Campaign goals and notes..."
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-fg-muted block mb-1">Status</label>
            <Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-fg-muted block mb-1">Budget ($)</label>
            <Input
              size="sm"
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="5000"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-fg-muted block mb-1">Start Date</label>
            <Input size="sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-fg-muted block mb-1">End Date</label>
            <Input size="sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-fg-muted block mb-1">Target Influencer Count</label>
          <Input
            size="sm"
            type="number"
            value={targetCount}
            onChange={(e) => setTargetCount(e.target.value)}
            placeholder="20"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim()}>
            {campaign ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Stat Card ───────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex-shrink-0 bg-surface-raised border border-edge rounded-lg px-3 py-2 min-w-[100px]">
      <div className="text-lg font-semibold text-fg">{value}</div>
      <div className="text-[10px] text-fg-faint">{label}</div>
    </div>
  );
}
